import { TopicsTreeUrlParts } from '../../model';
import {
  AssetTreeNode,
  AssetTreeNodeType,
  CONTAINER_NODE_TYPES,
  NON_VISIBLE_ASSET_NODE_TYPES,
} from '../../model/asset-tree-node.model';

export function traverseTree(
  assetTree: AssetTreeNode,
  cb: (assetNode: AssetTreeNode, level: number) => void,
  { skipNotDisplayed = false } = {},
) {
  return _traverseTree(assetTree, cb, 0, skipNotDisplayed);
}

function _traverseTree(
  assetTree: AssetTreeNode,
  cb: (assetNode: AssetTreeNode, level: number) => void,
  level: number,
  skipNotDisplayed: boolean,
) {
  const visible = isVisibleNode(assetTree);
  if (!skipNotDisplayed || visible) {
    cb(assetTree, level);
  }

  const nextLevel = skipNotDisplayed && !visible ? level : level + 1;

  for (const child of Object.values(assetTree.children)) {
    _traverseTree(child, cb, nextLevel, skipNotDisplayed);
  }
}

export function findInTree(
  assetTree: AssetTreeNode,
  cb: (assetNode: AssetTreeNode) => boolean,
): AssetTreeNode | undefined {
  const result = cb(assetTree);

  if (result) {
    return assetTree;
  }

  for (const child of Object.values(assetTree.children)) {
    const childResult = findInTree(child, cb);

    if (childResult) {
      return childResult;
    }
  }

  return undefined;
}

export function isChildAssetNode(node: AssetTreeNode): boolean {
  return !!node.asset && !!node.parent?.asset;
}

export function isTopAssetNode(node: AssetTreeNode): boolean {
  return !!node.asset && (!node.parent || !node.parent.asset);
}

export function getRootNode(assetTreeNode: AssetTreeNode): AssetTreeNode {
  let node: AssetTreeNode = assetTreeNode;

  while (node.parent) {
    node = node.parent;
  }

  return node;
}

export function getSourceDirectory(assetTreeNode: AssetTreeNode): AssetTreeNode | undefined {
  let node: AssetTreeNode = assetTreeNode;

  while (node.parent) {
    node = node.parent;

    if (node.nodeType === AssetTreeNodeType.SourceDirectory) {
      return node;
    }
  }

  return undefined;
}

export function getPathRelativeToAssetsContainer(assetTreeNode: AssetTreeNode): string {
  const pathParts = [];
  let node: AssetTreeNode | undefined = assetTreeNode;

  while (node) {
    if (node.nodeType === AssetTreeNodeType.AssetsContainer) {
      break;
    }

    pathParts.unshift(node.modelName);
    node = node.parent;
  }

  return pathParts.join('/');
}

export function getVisiblePathParts(assetTreeNode: AssetTreeNode, skipContainers = false): string[] {
  const pathParts: string[] = [];

  let node: AssetTreeNode | undefined = assetTreeNode;
  const skipTypes = skipContainers ? [...CONTAINER_NODE_TYPES] : [];

  while (node) {
    if (isVisibleNode(node) && !skipTypes.includes(node.nodeType)) {
      pathParts.unshift(node.modelName);
    }

    node = node.parent;
  }

  return pathParts;
}

/** Returns nodes that are not children of other nodes. */
export function filterOutCommonNodes(nodes: AssetTreeNode[]): AssetTreeNode[] {
  return nodes.filter((node) => {
    // Check if any other nodes are a parent of this node.
    const isChildOfAnotherNode = nodes.some((n) => n !== node && node.path.startsWith(n.path));

    // Return nodes that are not children.
    return !isChildOfAnotherNode;
  });
}

export function closestParent(
  node: AssetTreeNode | undefined,
  cb: (node: AssetTreeNode) => boolean,
): AssetTreeNode | undefined {
  let currentNode: AssetTreeNode | undefined = node;

  while (currentNode) {
    if (cb(currentNode)) {
      return currentNode;
    }

    currentNode = currentNode.parent;
  }

  return undefined;
}

export function getTopLevelAssetNodes(node: AssetTreeNode) {
  const assetsToMove: AssetTreeNode[] = [];

  traverseTree(node, (n) => {
    if (n.asset && !n.parent?.asset) {
      assetsToMove.push(n);
    }
  });

  return assetsToMove;
}

/**
 * Finds the node in the tree by the RELATIVE path.
 * A faster method for finding the given node than `findInTree`.
 *
 * Warning: the path is the asset tree path, not the `entity.$path`.
 */
export function findByPath(tree: AssetTreeNode, path: string) {
  if (path === tree.modelName) {
    return tree;
  }

  let node: AssetTreeNode = tree;
  const pathParts = path.split('/').slice(1);

  for (const part of pathParts) {
    const child = node.children[part];

    if (!child) {
      return undefined;
    }

    node = child;
  }

  return node;
}

export function haveSameRootAsset(node1: AssetTreeNode, node2: AssetTreeNode) {
  let root1 = node1;
  while (root1.parent && root1.parent.asset) {
    root1 = root1.parent;
  }

  let root2 = node2;
  while (root2.parent && root2.parent.asset) {
    root2 = root2.parent;
  }

  // Do not compare roots directly as the roots can be nodes from different trees (e.g. after tree rerender).
  return getPath(root1) === getPath(root2);
}

export function getPath(node: AssetTreeNode, filterFn: (node: AssetTreeNode) => boolean = () => true): string {
  const pathParts = [];

  let currentNode: AssetTreeNode | undefined = node;
  while (currentNode) {
    if (filterFn(currentNode)) {
      pathParts.unshift(currentNode.modelName);
    }
    currentNode = currentNode.parent;
  }

  return pathParts.join('/');
}

export function closestDirectory(node?: AssetTreeNode): AssetTreeNode | undefined {
  return node && closestParent(node, isTopicLevel1Node);
}

export function closestContainer(node?: AssetTreeNode): AssetTreeNode | undefined {
  return node && closestParent(node, isContainerNode);
}

export function isTopicLevel1Node(node: AssetTreeNode): node is AssetTreeNode<AssetTreeNodeType.TopicLevel1> {
  return node.nodeType === AssetTreeNodeType.TopicLevel1;
}

export function isContainerNode(node: AssetTreeNode): node is AssetTreeNode<AssetTreeNodeType.AssetsContainer> {
  return CONTAINER_NODE_TYPES.includes(node.nodeType);
}

export function isVisibleNode(node: AssetTreeNode): boolean {
  return !NON_VISIBLE_ASSET_NODE_TYPES.includes(node.nodeType);
}

export function isRootNode(node: AssetTreeNode): node is AssetTreeNode<AssetTreeNodeType.Root> {
  return node.nodeType === AssetTreeNodeType.Root;
}

export function getNodeRouterLink(node: AssetTreeNode): string | null {
  if (node.nodeType === AssetTreeNodeType.AssetsContainer) {
    return TopicsTreeUrlParts.Overview;
  }

  const topicLevel2Slug = node.asset?.slug;
  const topicLevel1Slug = node.parent?.asset?.slug;

  if (!(topicLevel1Slug && topicLevel2Slug)) {
    return null;
  }

  return `${topicLevel1Slug}/${topicLevel2Slug}`;
}
